import Controller from "@ember/controller";
import { action } from "@ember/object";
import { service } from "@ember/service";
import { dropTask, task, timeout } from "ember-concurrency";
import { cached, tracked } from "@glimmer/tracking";
import ENV from "glesys-controlpanel/config/environment";

const DEFAULT_SORT_BY = "snapshot";
const DEFAULT_SORT_DIR = "desc";

const byServer = (a, b) => (a.server.hostname || a.server.id).localeCompare(b.server.hostname || b.server.id);
const bySnapshot = (a, b) => new Date(a.snapshotTakenAt) - new Date(b.snapshotTakenAt);
const bySize = (a, b) => (a.diskInGiB ?? 0) - (b.diskInGiB ?? 0);

export default class ServersCreateFromBackupController extends Controller {
  @service intl;
  @service http;
  @service router;
  @service store;

  queryParams = ["sortBy", "sortDir", "serverFilter"];

  @tracked sortBy = null;
  @tracked sortDir = null;

  @tracked serverFilter = "";

  get tabs() {
    return [
      { name: this.intl.t("servers.create.tabs.distribution"), route: "servers.create.virtual" },
      { name: this.intl.t("servers.create.tabs.from-backup"), route: "servers.create.from-backup" },
      { name: this.intl.t("servers.create.tabs.one-click-installer"), route: "servers.appliance" },
    ];
  }

  get project() {
    return this.model.project;
  }

  get dataCenters() {
    return this.model.dataCenters;
  }

  get loadedBackups() {
    return this.loadBackups.lastSuccessful?.value ?? [];
  }

  @cached
  get serverOptions() {
    let servers = new Map();

    this.loadedBackups.forEach(({ server }) => {
      if (!servers.has(server.id)) {
        servers.set(server.id, { value: server.id, label: server.hostname || server.id });
      }
    });

    let sortedServers = [...servers.values()].toSorted((a, b) => a.label.localeCompare(b.label));

    return [{ value: "", label: this.intl.t("servers.create.from-backup.filter.all-servers") }, ...sortedServers];
  }

  get hasSelectedServer() {
    return this.serverOptions.some((option) => option.value === this.serverFilter) ? this.serverFilter : "";
  }

  get filteredBackups() {
    if (!this.hasSelectedServer) {
      return this.loadedBackups;
    }

    return this.loadedBackups.filter((backup) => backup.server.id === this.hasSelectedServer);
  }

  get selectedServerLabel() {
    return (
      this.serverOptions.find((option) => option.value === this.hasSelectedServer)?.label ?? this.serverOptions[0].label
    );
  }

  @action
  selectServer(value) {
    this.serverFilter = value;
  }

  @cached
  get sortOptions() {
    return [
      {
        label: this.intl.t("servers.create.from-backup.sort.group.date"),
        options: [
          {
            label: this.intl.t("servers.create.from-backup.sort.snapshot-desc"),
            query: { sortBy: "snapshot", sortDir: "desc" },
            compare: (a, b) => bySnapshot(b, a) || byServer(a, b),
          },
          {
            label: this.intl.t("servers.create.from-backup.sort.snapshot-asc"),
            query: { sortBy: "snapshot", sortDir: "asc" },
            compare: (a, b) => bySnapshot(a, b) || byServer(a, b),
          },
        ],
      },
      {
        label: this.intl.t("servers.create.from-backup.sort.group.server"),
        options: [
          {
            label: this.intl.t("servers.create.from-backup.sort.server-asc"),
            query: { sortBy: "server", sortDir: "asc" },
            compare: (a, b) => byServer(a, b) || bySnapshot(b, a),
          },
          {
            label: this.intl.t("servers.create.from-backup.sort.server-desc"),
            query: { sortBy: "server", sortDir: "desc" },
            compare: (a, b) => byServer(b, a) || bySnapshot(b, a),
          },
        ],
      },
      {
        label: this.intl.t("servers.create.from-backup.sort.group.size"),
        options: [
          {
            label: this.intl.t("servers.create.from-backup.sort.size-desc"),
            query: { sortBy: "size", sortDir: "desc" },
            compare: (a, b) => bySize(b, a) || byServer(a, b),
          },
          {
            label: this.intl.t("servers.create.from-backup.sort.size-asc"),
            query: { sortBy: "size", sortDir: "asc" },
            compare: (a, b) => bySize(a, b) || byServer(a, b),
          },
        ],
      },
    ];
  }

  get currentSort() {
    const sortBy = this.sortBy ?? DEFAULT_SORT_BY;
    const sortDir = this.sortDir ?? DEFAULT_SORT_DIR;
    const known = this.sortOptions
      .flatMap((item) => ("options" in item ? item.options : [item]))
      .some((option) => option.query.sortBy === sortBy && option.query.sortDir === sortDir);

    return known ? { sortBy, sortDir } : { sortBy: DEFAULT_SORT_BY, sortDir: DEFAULT_SORT_DIR };
  }

  restoreBackupTask = task(async (backup, dataCenter) => {
    const { content } = await this.http.request({
      url: `${ENV.apiBaseUri}/server/createfrombackup`,
      method: "POST",
      body: { backupid: backup.id, datacenter: dataCenter },
    });

    this.store.pushPayload("server", { server: content.server });

    this.router.transitionTo("server.details", content.server.serverid);
  });

  loadBackups = task(async () => {
    let { content } = await this.http.withContext(this.project.id).request({
      url: `${ENV.apiBaseUri}/server/listbackups?projectkey=${this.project.id}`,
    });

    if (ENV.environment !== "test") {
      this.scheduleReload.perform();
    }

    return content.backups.map((backup) => ({
      ...backup,
      server: {
        id: backup.sourceServer.serverId,
        hostname: backup.sourceServer.hostname,
        deleted: Boolean(backup.sourceServer.deletedAt),
        deletedAt: backup.sourceServer.deletedAt,
        cpucores: backup.cpuCores,
        memorysize: backup.memoryInMiB,
        disksize: backup.diskInGiB,
        bandwidth: 0,
      },
    }));
  });

  scheduleReload = dropTask(async () => {
    await timeout(60000);
    this.loadBackups.perform();
  });
}
